import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Phone, Mail, MapPin } from "lucide-react";
import api from "@/lib/api";

const services = [
  "Executive & Convoy Security",
  "Life Support & Logistics",
  "Industrial Supply & Trade",
  "Engineering & EPC",
  "Aviation Ground Logistics",
  "Ports & Heavy Handling",
  "General enquiry",
];

const initial = {
  name: "",
  email: "",
  phone: "",
  company: "",
  subject: "",
  service_interest: "",
  message: "",
};

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Contact() {
  const [values, setValues] = useState(initial);
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setValues((v) => ({ ...v, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!values.name || !values.email || !values.message) {
      toast.error("Please provide your name, email and a message.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", values);
      toast.success("Message received. We'll be in touch shortly.");
      setValues(initial);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      className="relative py-32 md:py-40 border-t border-navy-border"
      data-testid="contact"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-24">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="lg:col-span-5"
        >
          <div className="flex items-center gap-4 mb-6">
            <span className="h-px w-10 bg-gold" />
            <span className="overline">Get In Touch</span>
          </div>
          <h2 className="h-display text-4xl md:text-6xl leading-[1.03]">
            Tell us where<br />
            <span className="text-gold">you&apos;re operating.</span>
          </h2>
          <p className="mt-8 text-white/60 max-w-md leading-[1.85]">
            Every engagement begins with a discreet conversation. Share the
            geography, the mission, and the timeline — we&apos;ll respond with a
            tailored capability brief within one business day.
          </p>

          <div className="mt-12 space-y-8">
            <a
              href="tel:+12482693506"
              className="group flex items-start gap-5"
              data-testid="contact-phone-link"
            >
              <span className="mt-1 h-9 w-9 border border-navy-border flex items-center justify-center text-gold group-hover:border-gold transition-colors">
                <Phone size={15} strokeWidth={1.4} />
              </span>
              <div>
                <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/50">
                  Direct line
                </div>
                <div className="font-display text-2xl mt-1 group-hover:text-gold transition-colors">
                  (248) 269-3506
                </div>
              </div>
            </a>

            <a
              href="mailto:mendsinternationalgroup@gmail.com"
              className="group flex items-start gap-5"
              data-testid="contact-email-link"
            >
              <span className="mt-1 h-9 w-9 border border-navy-border flex items-center justify-center text-gold group-hover:border-gold transition-colors">
                <Mail size={15} strokeWidth={1.4} />
              </span>
              <div>
                <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/50">
                  Enquiries
                </div>
                <div className="font-display text-lg mt-1 group-hover:text-gold transition-colors break-all">
                  mendsinternationalgroup@gmail.com
                </div>
              </div>
            </a>

            <div className="flex items-start gap-5">
              <span className="mt-1 h-9 w-9 border border-navy-border flex items-center justify-center text-gold">
                <MapPin size={15} strokeWidth={1.4} />
              </span>
              <div>
                <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/50">
                  Headquarters
                </div>
                <div className="font-display text-2xl mt-1">United States</div>
                <div className="font-mono text-[11px] tracking-[0.15em] uppercase text-white/50 mt-1">
                  Silent partners · Jordan · Beijing
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="lg:col-span-7 border border-navy-border bg-navy-surface/40 p-8 md:p-12"
          data-testid="contact-form"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8">
            <Field
              label="Full name"
              name="name"
              value={values.name}
              onChange={onChange}
              testId="contact-name"
              required
            />
            <Field
              label="Email"
              name="email"
              type="email"
              value={values.email}
              onChange={onChange}
              testId="contact-email"
              required
            />
            <Field
              label="Phone"
              name="phone"
              value={values.phone}
              onChange={onChange}
              testId="contact-phone"
            />
            <Field
              label="Company / Organisation"
              name="company"
              value={values.company}
              onChange={onChange}
              testId="contact-company"
            />
            <div className="md:col-span-2 flex flex-col gap-2">
              <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/50">
                Interested in
              </label>
              <select
                name="service_interest"
                value={values.service_interest}
                onChange={onChange}
                className="bg-transparent border-b border-navy-border px-0 py-3 text-white focus:outline-none focus:border-gold transition-colors font-sans"
                data-testid="contact-service"
              >
                <option value="" className="bg-navy-base">Select a capability…</option>
                {services.map((s) => (
                  <option key={s} value={s} className="bg-navy-base">
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <Field
              label="Subject"
              name="subject"
              value={values.subject}
              onChange={onChange}
              testId="contact-subject"
              className="md:col-span-2"
            />
            <div className="md:col-span-2 flex flex-col gap-2">
              <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/50">
                Message *
              </label>
              <textarea
                name="message"
                value={values.message}
                onChange={onChange}
                rows={5}
                required
                className="bg-transparent border-b border-navy-border px-0 py-3 text-white resize-none focus:outline-none focus:border-gold transition-colors font-sans leading-[1.7]"
                data-testid="contact-message"
              />
            </div>
          </div>

          <div className="mt-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <p className="text-white/40 text-xs max-w-md font-sans leading-[1.7]">
              By submitting, you consent to Mends International Group contacting you
              about your enquiry. Communications are treated as confidential.
            </p>
            <button
              type="submit"
              disabled={loading}
              className="btn-gold disabled:opacity-50"
              data-testid="contact-submit"
            >
              {loading ? "Sending…" : "Send Enquiry"}
              <span aria-hidden>→</span>
            </button>
          </div>
        </motion.form>
      </div>
    </section>
  );
}

function Field({ label, name, value, onChange, type = "text", testId, required, className = "" }) {
  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/50">
        {label}{required ? " *" : ""}
      </label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        className="bg-transparent border-b border-navy-border px-0 py-3 text-white focus:outline-none focus:border-gold transition-colors font-sans"
        data-testid={testId}
      />
    </div>
  );
}
